import { ref, computed, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStore } from '@/stores/SessionStore'
import { useUserStore } from '@/stores/UserStore'
import { useSpotStore } from '@/stores/SpotStore'
import { useCachedFetch } from '@/composables/useFetch'
import { useDateHelper } from '@/composables/useDateHelper'
import { useUnitConverter } from '@/composables/useUnitConverter'
import { format, isSameDay, isWithinInterval, isAfter, endOfDay, startOfDay } from 'date-fns'
import useStorageSystem from '@/composables/useStorageSystem'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals';

const { $BASEURL } = useConfigurationGlobals();

export let useTidesStore = defineStore('tidesStore', () => {
  const sessionStore = useSessionStore();
  const userStore = useUserStore();
  const spotStore = useSpotStore();

  const dateHelper = useDateHelper();
  const unitConverter = useUnitConverter();


  const savedTideSpotTimeout = ref(null);

  const nearbyTideSpots = ref([]);
  const selectedNearbyTideSpot = ref(null);
  const tideDate = ref(new Date());
  const allData = ref([]);
  const tideSpotTimezoneOffset = ref(0);

  const [savedSelectedTideSpot, savedSelectedTideSpotLoaded] = useStorageSystem({}, 'savedSelectedTideSpot', 'apiUserStorage' )

  const adjustedTideDateTime = computed(() => dateHelper.adjustDateTimeFromTZOffsetToUserTZ(tideDate.value, tideSpotTimezoneOffset.value));
  const adjustedTideDate = computed(() => {
    if (!isSameDay(adjustedTideDateTime.value, tideDate.value)) {
      return isAfter(adjustedTideDateTime.value, tideDate.value) ? endOfDay(tideDate.value) : startOfDay(tideDate.value);
    }

    return adjustedTideDateTime.value;
  })

  const selectedDateDataSet = computed(() =>
    allData.value.find((dataSet) =>
      isWithinInterval(adjustedTideDate.value, {
        start: dateHelper.adjustDateTimeFromTZOffsetToUserTZ(
          dataSet.startDate,
          tideSpotTimezoneOffset.value
        ),
        end: dateHelper.adjustDateTimeFromTZOffsetToUserTZ(
          dataSet.endDate,
          tideSpotTimezoneOffset.value
        )
      })
    )
  )
  
  const tideData = computed(() =>
    selectedDateDataSet.value
      ? selectedDateDataSet.value.tides?.data.filter((dataPoint) =>
          isSameDay(new Date(dataPoint[0]), adjustedTideDate.value)
        )
      : []
  )
  const tideDataMin = computed(() => selectedDateDataSet.value.tides?.minValue || null);
  const tideDataMax = computed(() => selectedDateDataSet.value.tides?.maxValue || null);
  const tideEvents = computed(() => selectedDateDataSet.value ? selectedDateDataSet.value.tideEvents?.data.filter(dataPoint => isSameDay(new Date(dataPoint.dateTime), tideDate.value)) : []);
  const solunarEvents = computed(() => selectedDateDataSet.value ? selectedDateDataSet.value.solunarEvents?.data.filter(dataPoint => isSameDay(new Date(dataPoint.dateTime), tideDate.value)) : []);
  const hasCurrentsSpot = computed(() => nearbyTideSpots.value.some(spot => spot.type === 'currents'));
  const nextTideEvent = computed(() => tideEvents.value.find(event => event.isNext) || null);
  const nowTideValues = computed(() => {

    const nowTideValueDateTime = getTideSpotLocalDate( selectedDateDataSet.value.tides?.nowTideValues[0], tideSpotTimezoneOffset.value )
    const tideSpotLocalTideDate = adjustedTideDateTime.value

    return isSameDay(nowTideValueDateTime, tideSpotLocalTideDate) ? selectedDateDataSet.value.tides?.nowTideValues : [null, null]
  })

  const sunrise = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('sunrise')) || null);
  const sunset = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('sunset')) || null);
  const moonrise = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('moonrise')) || null);
  const moonset = computed(() => solunarEvents.value.find(event => event.eventName.toLowerCase().includes('moonset')) || null);
  const moonStatus = computed(
    () =>
      solunarEvents.value.find(
        (event) =>
          !event.eventName.toLowerCase().includes('moonset') &&
          !event.eventName.toLowerCase().includes('moonrise') &&
          !event.eventName.toLowerCase().includes('sun')
      ) || null
  );

  async function getNearbyTideSpotsByLatLon(lat, lon) {
    const latLon = `${lat}:${lon}`;
    const distance = unitConverter.convert('mi', userStore.units.distance, 20, true)
    const data = await getSpotSetBySearch(latLon, { search_dist: distance, spot_types: '10,18' })

    if (data.error) return data;

    const processedData = transformTideSpotData(data.data_values);

    nearbyTideSpots.value = processedData;

    return data; 
  }

  async function getSpotSetBySearch(search, settings = {}) {
    const defaultSettings = {
      units_wind: userStore.units.wind,
      units_temp: userStore.units.temp,
      units_distance: userStore.units.distance,
      units_precip: userStore.units.precip,
      include_spot_products: false,
      stormprint_only: false,
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    params.search = search;

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/spot/getSpotSetBySearch?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getTideData(settings) {
    const defaultSettings = {
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/tide/getTideData?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getTideEvents(settings) {
    const defaultSettings = {
      wf_token: sessionStore.token
    }

    const params = Object.assign(defaultSettings, settings)

    const queryString = new URLSearchParams(params).toString()
    const url = `${$BASEURL}/tide/getTideEvents?${queryString}`

    try {
      const { error, data } = await useCachedFetch(url)

      if (error.value) {
        throw error.value
      }

      if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
        throw data.value.status || data.value.status_code
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return { error: true, message: error }
    }
  }

  async function getAllTideDataByDateRange(settings) {
    if (!settings.tide_site_name || !settings.begin_time || !settings.end_time) return;

    const startDate = settings.begin_time;
    const endDate = settings.end_time;
    const dateRangeCount =  settings.date_range_count || 1;

    settings.begin_time = format(new Date(settings.begin_time), 'yyyy-MM-dd HH:mm:ss');
    settings.end_time = format(new Date(settings.end_time), 'yyyy-MM-dd HH:mm:ss');

    const responseData = await Promise.allSettled([getTideData(settings), getTideEvents(settings)]);
    const repsonseDateCopy = JSON.parse(JSON.stringify(responseData));

    tideSpotTimezoneOffset.value = getTimezoneOffsetHours(repsonseDateCopy[0].value?.tidedata[0].dateTime)

    const processedTideData = processTideData(repsonseDateCopy[0].value);

    const nowTideValues = processedTideData[1];
    const minValue = processedTideData[2];
    const maxValue = processedTideData[3];
    
    const processedEventData = processTideEventsData(repsonseDateCopy[1].value);

    const tideDataObject = {
      data: processedTideData[0],
      nowTideValues,
      minValue,
      maxValue
    }
    
    const eventDataObject = {
      data: processedEventData[0]
    }

    const solunarDataObject = {
      data: processedEventData[1]
    }

    const responseDataObject = {
      tides: tideDataObject,
      tideEvents: eventDataObject,
      solunarEvents: solunarDataObject,
      startDate,
      endDate
    }

    if (allData.value.length > dateRangeCount) allData.value = [];
    
    allData.value.push(responseDataObject);
    
    return responseDataObject
  }


  function transformTideSpotData(data) {
    return data.map((spot) => {
      const name = spot[1]
      const nameParts = name.split(', ')
      const locationName = nameParts.shift()

      return {
        id: spot[0],
        name: locationName,
        region: nameParts.join(', '),
        location: name,
        type: spot[2] === 10 ? 'tide' : 'currents',
        units: spot[2] === 10 ? 'ft' : 'kts',
        distance: spot[4],
        lat: spot[5],
        lon: spot[6]
      }
    })
  }  

  function processTideData(data) {
    const transformedData = transformTideData(data.tidedata);
    const nowTideValues = calculateNowTideValues(transformedData[0]);

    return [transformedData[0], nowTideValues, transformedData[1], transformedData[2]]
  }

  function transformTideData(data) {
    if (!data) return;

    let maxValue = -Infinity;
    let minValue = Infinity;

    const transformedTideData = data.map((dataPoint, i) => {

      const eventValue = parseFloat(dataPoint.eventValue);

      maxValue = Math.max(maxValue, parseFloat(dataPoint.eventValue));
      minValue = Math.min(minValue, parseFloat(dataPoint.eventValue));

      return [getTideSpotLocalDate(dataPoint.dateTime, tideSpotTimezoneOffset.value).getTime(), parseFloat(eventValue)]
    });

    return [transformedTideData, minValue, maxValue];
  }

  function processTideEventsData(data) {
    const solunarTerms = ['sun', 'moon', 'eclipse', 'quarter', 'solstice']

    const tideEvents = []
    const solunarEvents = []

    data.tideEvents.forEach((event) => {
      event.dateTime = getTideSpotLocalDate(event.dateTime, tideSpotTimezoneOffset.value).getTime()

      const isSolarEvent = solunarTerms.find((term) => event.eventName.toLowerCase().includes(term));

      if (isSolarEvent) {
        solunarEvents.push(event)
      } else {
        tideEvents.push(event)
      }
    })

    const transformedTideEvents = transformTideEvents(tideEvents)

    return [transformedTideEvents, solunarEvents]
  }  

  function getTideSpotLocalDate(date, tideSpotUTCOffset, debug) {
    return dateHelper.adjustDateTimeFromUserTZToTZOffset(date, tideSpotUTCOffset, debug);
  }

  function getTimezoneOffsetHours(date) {
    const match = date.match(/([+-])([0-9]{2}):([0-9]{2})$/)

    if (match) {
      const sign = match[1] === '+' ? 1 : -1
      const hours = parseInt(match[2], 10)
      const offsetHours = sign * hours;

      return offsetHours;
    }

    return 0;
  }

  function setSelectedNearbyTideSpot(tideSpotId, saveTideSpot = true) {
    if (selectedNearbyTideSpot.value && tideSpotId === selectedNearbyTideSpot.value?.id) return

    const tideSpot = tideSpotId ? getTideSpotById(tideSpotId) : null;
    
    selectedNearbyTideSpot.value = tideSpot || nearbyTideSpots.value.find(spot => spot.type === 'tide') || nearbyTideSpots.value[0] || null;

    if (saveTideSpot && tideSpot) saveLastSelectedTideSpot(selectedNearbyTideSpot.value.id);

    allData.value = [];
  }

  function saveLastSelectedTideSpot(tideSpotId) {
    savedSelectedTideSpot.value[spotStore.spotId] = tideSpotId;
  }

  function transformTideEvents(tideEvents) {
    if (tideEvents.length) {
      let nextEventFlagged = false;
      
      return tideEvents.map((event, i) => {
        event.iconName = event.eventName.toLowerCase().replaceAll(',', '').replaceAll(' ', '-');
        event.timeFormatted = `${format(event.dateTime, 'h:mm a')} <span>${spotStore.localTimezoneShort}</span>`
        event.isNext = false;
        event.isPrev = false;
        event.isPast = false;
        event.isFuture = false;
        event.type = selectedNearbyTideSpot.value?.type || 'tide';

        if (event.dateTime > dateHelper.adjustDateTimeFromUserTZToTZOffset(new Date(), tideSpotTimezoneOffset.value).getTime()) {
          event.isFuture = true

          if (!nextEventFlagged) {
            event.isNext = true
            nextEventFlagged = true

            if (tideEvents[i - 1]) tideEvents[i - 1].isPrev = true
          }
        } else {
          event.isPast = true
        }

        return event;
      });
    }
  }

  function getTideSpotById(id) {
    return nearbyTideSpots.value.find(spot => spot.id === id);
  }

  function calculateNowTideValues (data) {
    let [prevIndex, nextIndex] = getPrevAndNextIndexes(data);

    if (prevIndex === null || nextIndex === null || prevIndex === -1 || nextIndex === -1) return [null, null, data];
    
    const now = getTideSpotLocalDate(new Date(), tideSpotTimezoneOffset.value)
    const nowBeforeTimeDelta = now - data[prevIndex][0]
    const currentTimePeriodLength = data[nextIndex][0] - data[prevIndex][0]
    const currentTimePeriodCompletionPercentage = nowBeforeTimeDelta / currentTimePeriodLength
    const interpolatedYValue = parseFloat(((data[nextIndex][1] - data[prevIndex][1]) * currentTimePeriodCompletionPercentage + data[prevIndex][1]).toFixed(2))
    const status = data[prevIndex][1] < data[nextIndex][1] ? 'rising' : 'falling'

    data.splice(prevIndex + 1, 0, [now.getTime(), interpolatedYValue])
    return [now, interpolatedYValue, status, data];
  }

  function getPrevAndNextIndexes(data) {
    if (!data) return [null, null];

    const now = getTideSpotLocalDate(new Date(), tideSpotTimezoneOffset.value).getTime();
    const nextIndex = data.findIndex((element) => element[0] > now)
    let prevIndex = null;
    // const prevIndex = data.findLastIndex((element) => element[0] < now)
    data.forEach((element, i) => {
      if (element[0] < now) prevIndex = i;
    })

    return [prevIndex, nextIndex]
  }

  function getDateRangeSolunarEvents(startDate, endDate) {
    if (!startDate || !endDate || !allData.value.length) return [];

    const adjustedStartDate = dateHelper.adjustDateTimeFromUserTZToTZOffset(startDate, tideSpotTimezoneOffset.value);
    const adjustedEndDate = dateHelper.adjustDateTimeFromUserTZToTZOffset(endDate, tideSpotTimezoneOffset.value);

    const dateRangeData = allData.value.filter(dataSet => {
      const adjustedDataSetStartDate = dateHelper.adjustDateTimeFromUserTZToTZOffset(dataSet.startDate, tideSpotTimezoneOffset.value);
      const adjustedDataSetEndDate = dateHelper.adjustDateTimeFromUserTZToTZOffset(dataSet.endDate, tideSpotTimezoneOffset.value);

      return adjustedStartDate.getTime() <= adjustedDataSetStartDate.getTime() && adjustedEndDate.getTime() >= adjustedDataSetEndDate.getTime()
    });

    return dateRangeData.length ? dateRangeData[0].solunarEvents.data : [];
  }

  function allDataCleanup() {
    // Keep only the data set that includes the currently selected date
    allData.value = [selectedDateDataSet.value];
  }

  function loadInitialTideSpot() {
    // There being no tide spot selected means the tide container just loaded and a spot needs to be selected
    // either a saved one (if it exists) or the default one.
    if (nearbyTideSpots.value.length && !selectedNearbyTideSpot.value) {
      if (
        Object.keys(savedSelectedTideSpot.value).length &&
        savedSelectedTideSpot.value[`${spotStore.spotId}`]
      ) {
        setSelectedNearbyTideSpot(savedSelectedTideSpot.value[`${spotStore.spotId}`], false)
        
        return
      }
      
      setSelectedNearbyTideSpot(null, false)
    }
  }

  watchEffect(() => {
    savedTideSpotTimeout.value = setTimeout(() => {
      loadInitialTideSpot();
    }, 5000);

    if (!savedSelectedTideSpotLoaded.value || selectedNearbyTideSpot.value) return;

    if (savedTideSpotTimeout.value) {
      clearTimeout(savedTideSpotTimeout.value);
      savedTideSpotTimeout.value = null;
    }

    loadInitialTideSpot();
  });

  return {
    adjustedTideDateTime,
    selectedDateDataSet,
    allData,
    hasCurrentsSpot,
    moonrise,
    moonset,
    moonStatus,
    nearbyTideSpots,
    nextTideEvent,
    nowTideValues,
    selectedNearbyTideSpot,
    solunarEvents,
    sunrise,
    sunset,
    tideData,
    tideDataMin,
    tideDataMax,
    tideDate,
    tideEvents,
    tideSpotTimezoneOffset,
    allDataCleanup,
    getDateRangeSolunarEvents,
    getNearbyTideSpotsByLatLon,
    getAllTideDataByDateRange,
    getTideData,
    getTideEvents,
    setSelectedNearbyTideSpot
  }
})
